import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "The Pleiades",
    subtitle: "M45 - Seven Sisters in Taurus",
    intro: "The Pleiades, also known as Seven Sisters and Messier 45 (M45), is an asterism of an open star cluster containing young B-type stars in the northwest of the constellation Taurus. At a distance of about 444 light-years, it is among the nearest star clusters to Earth and the nearest Messier object to Earth, being the most obvious star cluster to the naked eye in the night sky.",
    nebulae: "Reflection Nebulae",
    nebulaeDesc: "It contains the reflection nebulae NGC 1432, an HII region, and NGC 1435, known as the Merope Nebula. Around 2330 BC the Pleiades marked the vernal point. Due to the brightness of its stars, the Pleiades is viewable from most areas on Earth, even in locations with significant light pollution.",
    composition: "Stellar Composition",
    compositionDesc: "The cluster is dominated by hot blue luminous stars that have formed within the last 100 million years. Reflection nebulae around the brightest stars were once thought to be leftover material from their formation but are now considered likely to be an unrelated dust cloud in the interstellar medium through which the stars are currently passing. This dust cloud is estimated to be moving at a speed of approximately 18 km/s relative to the stars in the cluster.",
    formation: "Formation & Future",
    formationDesc: "Computer simulations have shown that the Pleiades were probably formed from a compact configuration that once resembled the Orion Nebula. Astronomers estimate that the cluster will survive for approximately another 250 million years, after which the clustering will be lost due to gravitational interactions with the galactic neighborhood.",
    goldenGate: "Golden Gate of the Ecliptic",
    goldenGateDesc: "Together with the open star cluster of the Hyades, the Pleiades form the Golden Gate of the Ecliptic. The Pleiades have been said to 'resemble a tiny dipper,' and should not be confused with the 'Little Dipper,' or Ursa Minor.",
    nameOrigin: "Origin of Name",
    nameOriginDesc: "The name Pleiades comes from Ancient Greek: Πλειάδες. It probably derives from plein (πλεῖν 'to sail') because of the cluster's importance in delimiting the sailing season in the Mediterranean Sea: 'the season of navigation began with their heliacal rising.' In Classical Greek mythology, the name was used for seven divine sisters called the Pleiades."
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Pleiadene",
    subtitle: "M45 - Syvstjernen i Tyren",
    intro: "Pleiadene, også kjent som Syvstjernen og Messier 45 (M45), er en asterisme av en åpen stjernehop som inneholder unge B-type stjerner i nordvest av stjernebildet Tyren. Med en avstand på omtrent 444 lysår er den blant de nærmeste stjernehopene til Jorden og det nærmeste Messier-objektet til Jorden, og er den mest åpenbare stjernehopen for det blotte øye på nattehimmelen.",
    nebulae: "Refleksjonståker",
    nebulaeDesc: "Den inneholder refleksjonståkene NGC 1432, en HII-region, og NGC 1435, kjent som Merope-tåken. Rundt 2330 f.Kr. markerte Pleiadene vårpunktet. På grunn av lysstyrken til stjernene er Pleiadene synlige fra de fleste områder på Jorden, selv på steder med betydelig lysforurensning.",
    composition: "Stjernesammensetning",
    compositionDesc: "Hopen domineres av varme blå lysende stjerner som har dannet seg i løpet av de siste 100 millioner årene. Refleksjonståker rundt de lyseste stjernene ble en gang antatt å være restmateriale fra deres dannelse, men anses nå sannsynligvis å være en urelatert støvsky i det interstellare mediet som stjernene for tiden passerer gjennom. Denne støvskyen anslås å bevege seg med en hastighet på omtrent 18 km/s i forhold til stjernene i hopen.",
    formation: "Dannelse og Fremtid",
    formationDesc: "Datasimuleringer har vist at Pleiadene sannsynligvis ble dannet fra en kompakt konfigurasjon som en gang lignet Oriontåken. Astronomer anslår at hopen vil overleve i omtrent 250 millioner år til, hvoretter klyngen vil gå tapt på grunn av gravitasjonsinteraksjoner med det galaktiske nabolaget.",
    goldenGate: "Ekliptikkens Gyldne Port",
    goldenGateDesc: "Sammen med den åpne stjernehopen Hyadene danner Pleiadene Ekliptikkens Gyldne Port. Pleiadene har blitt sagt å 'ligne en liten øse', og bør ikke forveksles med 'Lille Bjørn' eller Ursa Minor.",
    nameOrigin: "Navnets Opprinnelse",
    nameOriginDesc: "Navnet Pleiades kommer fra gammelgresk: Πλειάδες. Det stammer sannsynligvis fra plein (πλεῖν 'å seile') på grunn av hopens betydning for å avgrense seilesesongen i Middelhavet: 'navigasjonssesongen begynte med deres heliakale oppgang.' I klassisk gresk mytologi ble navnet brukt om syv guddommelige søstre kalt Pleiadene."
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Plejady",
    subtitle: "M45 - Siedem Sióstr w Byku",
    intro: "Plejady, znane również jako Siedem Sióstr i Messier 45 (M45), to asteryzm otwartej gromady gwiazd zawierającej młode gwiazdy typu B w północno-zachodniej części gwiazdozbioru Byka. W odległości około 444 lat świetlnych jest to jedna z najbliższych gromad gwiazd od Ziemi i najbliższy obiekt Messiera od Ziemi, będąc najbardziej oczywistą gromadą gwiazd widoczną gołym okiem na nocnym niebie.",
    nebulae: "Mgławice Refleksyjne",
    nebulaeDesc: "Zawiera mgławice refleksyjne NGC 1432, region HII, oraz NGC 1435, znaną jako Mgławica Merope. Około 2330 p.n.e. Plejady wyznaczały punkt wiosenny. Ze względu na jasność swoich gwiazd, Plejady są widoczne z większości obszarów Ziemi, nawet w miejscach ze znacznym zanieczyszczeniem świetlnym.",
    composition: "Skład Gwiazdowy",
    compositionDesc: "Gromada jest zdominowana przez gorące niebieskie świecące gwiazdy, które powstały w ciągu ostatnich 100 milionów lat. Mgławice refleksyjne wokół najjaśniejszych gwiazd uważano kiedyś za pozostałości po ich formowaniu, ale teraz uważa się, że prawdopodobnie są niezwiązaną chmurą pyłu w środowisku międzygwiazdowym, przez którą gwiazdy obecnie przechodzą. Szacuje się, że ta chmura pyłu porusza się z prędkością około 18 km/s względem gwiazd w gromadzie.",
    formation: "Formowanie i Przyszłość",
    formationDesc: "Symulacje komputerowe wykazały, że Plejady prawdopodobnie powstały z kompaktowej konfiguracji, która kiedyś przypominała Mgławicę Oriona. Astronomowie szacują, że gromada przetrwa jeszcze około 250 milionów lat, po czym skupisko rozpadnie się z powodu oddziaływań grawitacyjnych z galaktycznym sąsiedztwem.",
    goldenGate: "Złota Brama Ekliptyki",
    goldenGateDesc: "Wraz z otwartą gromadą gwiazd Hiady, Plejady tworzą Złotą Bramę Ekliptyki. Mówi się, że Plejady 'przypominają mały czerpak' i nie należy ich mylić z 'Małym Wozem' lub Małą Niedźwiedzicą.",
    nameOrigin: "Pochodzenie Nazwy",
    nameOriginDesc: "Nazwa Plejady pochodzi ze starożytnej greki: Πλειάδες. Prawdopodobnie wywodzi się od plein (πλεῖν 'żeglować') ze względu na znaczenie gromady w wyznaczaniu sezonu żeglarskiego na Morzu Śródziemnym: 'sezon nawigacji rozpoczynał się od ich wschodu heliakalnego.' W klasycznej mitologii greckiej nazwa była używana dla siedmiu boskich sióstr zwanych Plejadami."
  }
};

export default function PleiadesDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-cyan-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
                data-testid="lang-pl-btn"
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="pleiades-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="pleiades-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Overview */}
            <div>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.intro}
              </p>
            </div>

            {/* Nebulae */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.nebulae}
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.nebulaeDesc}
              </p>
            </div>

            {/* Composition */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.composition}
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.compositionDesc}
              </p>
            </div>

            {/* Formation */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.formation}
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.formationDesc}
              </p>
            </div>

            {/* Golden Gate */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.goldenGate}
              </h2>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.goldenGateDesc}
              </p>
            </div>

            {/* Name Origin */}
            <div className="pt-6 border-t border-white/10">
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-cyan-300 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.nameOrigin}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {t.nameOriginDesc}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
